import { CallAPI } from "../../apiManager/ApiCall";
import {
  addPinURL,
  addSlideURL,
  addSubTitleURL,
  addTitleURL,
  editPinURL,
  getSlideByIdURL,
  getTitleURL,
  getLibraryURL,
  libraryReferenceURL,
  libraryVisibilityURL,
  swapTitleURL,
  editTitleImageCallURL,
  updatePresentationPublishURL,
} from "../../apiManager/APIConfig";
import { setTitle } from "../../redux/slices/titleSlice";
import { store } from "../../redux/store";

export const GetTitlesCall = async (lang, type) => {
  try {
    const response = await CallAPI({
      url: `${getTitleURL}?lang=${lang}&type=${type}`,
      method: "GET",
    });
    const list = Array.isArray(response) ? response : response?.data;
    if (Array.isArray(list)) {
      store.dispatch(setTitle(list));
      return list;
    }
    if (!response?.status) {
      store.dispatch(setTitle(response));
      return response;
    }
    return response;
  } catch (error) {
    return { status: 400 };
  }
};

/** Fetch titles without updating Redux (e.g. for browse-public modal). */
export const GetTitlesCallNoStore = async (lang, type) => {
  try {
    const response = await CallAPI({
      url: `${getTitleURL}?lang=${lang}&type=${type}`,
      method: "GET",
    });
    const list = Array.isArray(response) ? response : response?.data;
    return Array.isArray(list) ? list : [];
  } catch (error) {
    return [];
  }
};

export const AddTitlesCall = async (body) => {
  try {
    const response = await CallAPI({ url: addTitleURL, method: "POST", body });

    GetTitlesCall(body?.lang, body?.type);
    return response;
  } catch (error) {
    return { status: 400 };
  }
};

export const AddSlideCall = async (body) => {
  try {
    const response = await CallAPI({ url: addSlideURL, method: "POST", body });

    GetTitlesCall(body?.lang, body?.type);
    return response;
  } catch (error) {
    return { status: 400 };
  }
};

export const AddSubTitlesCall = async (body) => {
  try {
    const response = await CallAPI({
      url: addSubTitleURL,
      method: "POST",
      body,
    });

    GetTitlesCall(body?.lang, body?.type);
    return response;
  } catch (error) {
    return { status: 400 };
  }
};

export const EditSubTitlesCall = async (body) => {
  try {
    const response = await CallAPI({
      url: `${addSubTitleURL}`,
      method: "PUT",
      body,
    });
    await GetTitlesCall(body?.lang, body?.type);
    return response;
  } catch (error) {
    return { status: 400 };
  }
};
export const EditTitleImageCall = async (body) => {
  try {
    const response = await CallAPI({
      url: `${editTitleImageCallURL}`,
      method: "PUT",
      body,
    });
    GetTitlesCall(body?.lang, body?.type);
    return response;
  } catch (error) {
    return { status: 400 };
  }
};
export const EditTitlesCall = async (body) => {
  try {
    const response = await CallAPI({
      url: `${addTitleURL}`,
      method: "PUT",
      body,
    });
    await GetTitlesCall(body?.lang, body?.type);
    return response;
  } catch (error) {
    return { status: 400 };
  }
};
export const DeleteTitlesCall = async (body) => {
  try {
    const response = await CallAPI({
      url: `${addTitleURL}/${body?.id}`,
      method: "DELETE",
    });

    GetTitlesCall(body?.lang, body?.type);
    return response;
  } catch (error) {
    return { status: 400 };
  }
};
export const DeleteSubTitlesCall = async (body) => {
  try {
    const response = await CallAPI({
      url: `${addSubTitleURL}/${body?.id}`,
      method: "DELETE",
    });

    GetTitlesCall(body?.lang, body?.type);
    return response;
  } catch (error) {
    return { status: 400 };
  }
};

export const GetSlideByIdCall = async (body) => {
  try {
    const includePrivate = body?.includePrivatePins ? "&includePrivatePins=true" : "";
    const ts = body?.ts ? `&_ts=${encodeURIComponent(body.ts)}` : "";
    const response = await CallAPI({
      url: `${getSlideByIdURL}?id=${body?.id}&lang=${body?.lang}${includePrivate}${ts}`,
      method: "GET",
    });

    return response;
  } catch (error) {
    return { status: 400 };
  }
};

export const AddPinCall = async (body) => {
  try {
    const response = await CallAPI({ url: addPinURL, method: "POST", body });

    GetTitlesCall(body?.lang, body?.type);
    return response;
  } catch (error) {
    return { status: 400 };
  }
};

export const EditPinCall = async (body) => {
  try {
    const response = await CallAPI({
      url: `${editPinURL}`,
      method: "PUT",
      body,
    });

    GetTitlesCall(body?.lang, body?.type);
    return response;
  } catch (error) {
    return { status: 400 };
  }
};
export const DeletePinCall = async (id) => {
  try {
    const response = await CallAPI({
      url: `${editPinURL}/${id}`,
      method: "DELETE",
    });

    return response;
  } catch (error) {
    return { status: 400 };
  }
};

export const SwapTitlesCall = async (body) => {
  try {
    const response = await CallAPI({
      url: `${swapTitleURL}`,
      method: "PUT",
      body,
    });
    GetTitlesCall(body?.lang, body?.type);

    return response;
  } catch (error) {
    return { status: 400 };
  }
};

// Update publish / unpublish state for a presentation title.
// body: { title_id, is_published }
export const UpdatePresentationPublishCall = async (body) => {
  try {
    const response = await CallAPI({
      url: updatePresentationPublishURL,
      method: "PUT",
      body,
    });
    return response;
  } catch (error) {
    return { status: 400 };
  }
};

// My Library: private content + referenced public titles
export const GetLibraryCall = async (lang = "en") => {
  try {
    const response = await CallAPI({
      url: `${getLibraryURL}?lang=${lang}`,
      method: "GET",
    });
    if (response?.status === 200) {
      return { private: response.private || [], referenced: response.referenced || [] };
    }
    return { private: [], referenced: [] };
  } catch (error) {
    return { private: [], referenced: [] };
  }
};

// Library visibility per category (e.g. his / emb / three_d_model).
// Returns a map like: { his: 'none' | 'all' | 'private', emb: '...', three_d_model: '...' }
export const GetLibraryVisibilityCall = async () => {
  try {
    const response = await CallAPI({
      url: libraryVisibilityURL,
      method: "GET",
    });
    // Expect either { status: 200, data: { ... } } or a plain object.
    if (response?.status && response?.data) return response.data;
    if (response && !response?.status) return response;
    return {};
  } catch (error) {
    return {};
  }
};

// Update visibility for a single category.
// body: { category: 'his' | 'emb' | 'three_d_model', visibility: 'none' | 'all' | 'private' }
export const UpdateLibraryVisibilityCall = async (body) => {
  try {
    const response = await CallAPI({
      url: libraryVisibilityURL,
      method: "PUT",
      body,
    });
    return response;
  } catch (error) {
    return { status: 400 };
  }
};

export const AddLibraryReferenceCall = async (title_id, slide_id = null, target_subtitle_id = null, target_title_id = null) => {
  try {
    const body = { title_id };
    if (slide_id) body.slide_id = slide_id;
    if (target_subtitle_id) body.target_subtitle_id = target_subtitle_id;
    if (target_title_id) body.target_title_id = target_title_id;
    const response = await CallAPI({
      url: libraryReferenceURL,
      method: "POST",
      body,
    });
    return response;
  } catch (error) {
    return { status: 400 };
  }
};

export const RemoveLibraryReferenceCall = async (title_id, slide_id = null, target_subtitle_id = null) => {
  try {
    let url = `${libraryReferenceURL}/${title_id}`;
    if (slide_id && target_subtitle_id) {
      url += `?slide_id=${encodeURIComponent(slide_id)}&target_subtitle_id=${encodeURIComponent(target_subtitle_id)}`;
    }
    const response = await CallAPI({
      url,
      method: "DELETE",
    });
    return response;
  } catch (error) {
    return { status: 400 };
  }
};
